import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/cliente';
import type { EventoResumen, TipoEvento } from '../../api/tipos';
import { DialogoConfirmacion } from '../../componentes/DialogoConfirmacion';
import { EsqueletoListaAdmin } from '../../componentes/Esqueletos';
import { Vacio } from '../../componentes/Estados';
import { lineaCorta, textoConfirmados } from '../../lib/fechas';
import { COLOR_POR_DEFECTO } from '../../lib/personas';
import { FormularioEvento } from './FormularioEvento';
import { PanelTiposEvento } from './PanelTiposEvento';

/// null = no hay nada abierto · 'nuevo' = formulario en blanco · uuid = edición
type Seleccion = null | 'nuevo' | string;

export function VistaAdmin() {
  const [seleccion, setSeleccion] = useState<Seleccion>(null);
  const [tiposAbierto, setTiposAbierto] = useState(false);
  const [hayCambios, setHayCambios] = useState(false);
  /// A dónde se quería ir cuando había cambios sin guardar; espera confirmación.
  const [destinoPendiente, setDestinoPendiente] = useState<Exclude<Seleccion, null> | null>(null);

  const irA = (destino: Exclude<Seleccion, null>) => {
    if (destino === seleccion) return;
    if (hayCambios) setDestinoPendiente(destino);
    else setSeleccion(destino);
  };

  const eventos = useQuery({
    queryKey: ['admin', 'eventos'],
    queryFn: () => api.get<EventoResumen[]>('/admin/eventos'),
  });

  const tipos = useQuery({
    queryKey: ['admin', 'tipos-evento'],
    queryFn: () => api.get<TipoEvento[]>('/admin/tipos-evento'),
  });

  return (
    <>
      <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 items-start gap-5 px-4 pb-12 pt-6 sm:px-8 lg:grid-cols-[288px_minmax(0,1fr)]">
        <aside className="tarjeta p-4">
          <button
            type="button"
            onClick={() => irA('nuevo')}
            className="w-full rounded-[9px] bg-lima px-4 py-2.5 font-titulo text-[13.5px] font-bold tracking-[.3px] text-tinta transition-colors hover:bg-lima-claro"
          >
            + Nuevo evento
          </button>

          <button
            type="button"
            onClick={() => setTiposAbierto(true)}
            className="mt-2 w-full rounded-[9px] border border-white/15 px-4 py-2 text-[13px] text-suave transition-colors hover:bg-white/5"
          >
            Tipos de evento{tipos.data ? ` (${tipos.data.length})` : ''}
          </button>

          <div className="mb-2.5 mt-5 text-[11px] uppercase tracking-[1.3px] text-debil">
            Eventos{eventos.data ? ` (${eventos.data.length})` : ''}
          </div>

          {eventos.isPending ? (
            <EsqueletoListaAdmin />
          ) : eventos.data && eventos.data.length > 0 ? (
            <div className="flex max-h-[520px] flex-col gap-1.5 overflow-y-auto pr-1">
              {eventos.data.map((evento) => {
                const activo = seleccion === evento.id;
                return (
                  <button
                    key={evento.id}
                    type="button"
                    onClick={() => irA(evento.id)}
                    className={`flex items-center gap-2.5 rounded-[10px] border px-2.5 py-2.5 text-left transition-colors ${
                      activo
                        ? 'border-lima/45 bg-lima/10'
                        : 'border-transparent bg-white/[.035] hover:bg-white/[.06]'
                    }`}
                  >
                    <span
                      className="w-1 flex-none self-stretch rounded"
                      style={{ background: evento.tipo?.color ?? COLOR_POR_DEFECTO }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-semibold">
                        {evento.titulo}
                      </span>
                      <span className="mt-0.5 block truncate text-[11.5px] text-debil">
                        {lineaCorta(evento.fechaLocal, evento.horaLocal)} ·{' '}
                        {textoConfirmados(evento.confirmados)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-[12.5px] leading-relaxed text-mudo">
              Todavía no creaste ningún evento. Arrancá con «+ Nuevo evento».
            </p>
          )}
        </aside>

        <section className="tarjeta min-h-[520px] overflow-hidden">
          {seleccion === null ? (
            <Vacio
              titulo="Elegí un evento de la izquierda para editarlo, o creá uno nuevo."
              detalle="Desde acá se publican los eventos, se arman las preguntas de votación y se siguen los resultados en vivo."
            />
          ) : (
            <FormularioEvento
              key={seleccion}
              eventoId={seleccion === 'nuevo' ? null : seleccion}
              tipos={tipos.data ?? []}
              onCancelar={() => setSeleccion(null)}
              onGuardado={(id) => setSeleccion(id)}
              onEliminado={() => setSeleccion(null)}
              onCambiosPendientes={setHayCambios}
            />
          )}
        </section>
      </div>

      {tiposAbierto && <PanelTiposEvento onCerrar={() => setTiposAbierto(false)} />}

      {destinoPendiente && (
        <DialogoConfirmacion
          titulo="¿Salir sin guardar?"
          textoConfirmar="Salir sin guardar"
          textoCancelar="Seguir editando"
          tono="peligro"
          onConfirmar={() => {
            setSeleccion(destinoPendiente);
            setDestinoPendiente(null);
          }}
          onCancelar={() => setDestinoPendiente(null)}
        >
          Tenés cambios en este evento que todavía no guardaste. Si salís, se pierden.
        </DialogoConfirmacion>
      )}
    </>
  );
}
